import { useState, FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import EnLayout from '@/components/feature/EnLayout';
import useSEO from '@/hooks/useSEO';

// ─────────────────────────────────────────────
// Process page data
// ─────────────────────────────────────────────
const processSteps = [
  {
    num: '01',
    icon: 'ri-discuss-line',
    title: 'Needs Alignment',
    desc: 'Have an initial consultation with our sales specialist. Share your brand positioning, target audience, planned sales channels and budget range so we can confirm the direction of the collaboration.',
    duration: '1–3 business days',
    color: '#4CAF50',
  },
  {
    num: '02',
    icon: 'ri-flask-line',
    title: 'Formula Design',
    desc: 'Our R&D team proposes formula options, explains ingredient benefits, component ratios and preliminary pricing. After your confirmation, we proceed to produce a trial sample.',
    duration: '5–14 business days',
    color: '#43a047',
  },
  {
    num: '03',
    icon: 'ri-palette-line',
    title: 'Packaging Design',
    desc: 'We provide packaging specification recommendations, assist in confirming container selection, label layout, and review label copy for compliance with food labeling regulations.',
    duration: '7–14 business days',
    color: '#388e3c',
  },
  {
    num: '04',
    icon: 'ri-verified-badge-line',
    title: 'Testing & Certification',
    desc: 'Samples are sent to a third-party institution for safety testing, producing complete CoA (Certificate of Analysis) reports including microbial, heavy metal, and pesticide residue analyses.',
    duration: '10–20 business days',
    color: '#2e7d32',
  },
  {
    num: '05',
    icon: 'ri-settings-3-line',
    title: 'Mass Production Confirmation',
    desc: 'After signing the contract and confirming order quantities, full-scale GMP production begins. Quality control personnel oversee every process node.',
    duration: '4–8 weeks',
    color: '#1b5e20',
  },
  {
    num: '06',
    icon: 'ri-truck-line',
    title: 'Delivery Completed',
    desc: 'After the finished products pass QC acceptance, they are shipped according to the agreed schedule with complete shipping documents, followed by ongoing technical support.',
    duration: 'Per contract terms',
    color: '#4CAF50',
  },
];

const processHighlights = [
  { icon: 'ri-user-line', label: 'Dedicated account manager throughout' },
  { icon: 'ri-shield-check-line', label: 'NDA protects your formula confidentiality' },
  { icon: 'ri-time-line', label: 'Transparent timeline, always on track' },
  { icon: 'ri-file-check-line', label: 'Full documentation support for compliance' },
];

// ─────────────────────────────────────────────
// Services page data
// ─────────────────────────────────────────────
const coopServices = [
  {
    icon: 'ri-scales-3-line',
    tag: '01',
    title: 'Regulatory Risk Control',
    subtitle: 'Compliance before launch — build your brand on solid ground',
    desc: 'We help clarify food and health supplement regulations to reduce production and marketing risks. Kang Yong has extensive practical experience in food regulatory affairs. We conduct pre-formulation regulatory reviews to confirm ingredient compliance and that claims meet Taiwan TFDA requirements, so your product starts on a compliant foundation.',
    points: [
      'Positive ingredient list review to confirm legal usability',
      'Labeling text and advertising wording compliance guidance',
      'Health food certification (Green Man label) application support',
      'Export market regulatory difference consultation',
    ],
    img: 'https://readdy.ai/api/search-image?query=professional%20legal%20compliance%20document%20review%20health%20supplement%20regulatory%20affairs%20specialist%20checking%20ingredient%20list%20certification%20papers%20in%20clean%20office%2C%20minimal%20modern%20environment&width=640&height=420&seq=csvc-reg-01&orientation=landscape',
    cta: 'Learn About Regulatory Risk',
    ctaLink: '/en/about/regulation',
  },
  {
    icon: 'ri-quill-pen-line',
    tag: '02',
    title: 'Marketing Material Support',
    subtitle: 'Know the product — help you say the right things effectively',
    desc: 'We can assist with providing product-related marketing materials. From product introduction slides, promotional flyers, to website product images, we help clients better understand their products and promote quality nutrition to consumers accurately and effortlessly.',
    points: [
      'Product education and training slide decks',
      'Product promotional flyers and poster materials',
      'Compliant product messaging and wording suggestions',
    ],
    img: 'https://readdy.ai/api/search-image?query=creative%20marketing%20team%20working%20on%20health%20supplement%20product%20packaging%20design%20copywriting%20brainstorming%20on%20whiteboard%2C%20professional%20agency%20environment%2C%20clean%20bright%20workspace&width=640&height=420&seq=csvc-copy-01&orientation=landscape',
    cta: 'Inquire About Copy Services',
    ctaLink: '/en/cooperation/inquiry',
  },
  {
    icon: 'ri-ship-line',
    tag: '03',
    title: 'Imported Ingredient Sourcing',
    subtitle: 'Skip the hassle — quality international ingredients delivered',
    desc: 'We can assist with sourcing imported ingredients. Please contact us for related sourcing information. Kang Yong maintains long-term relationships with international ingredient suppliers and is familiar with import procedures, tariff declarations and quality certification requirements. We can help clients source high-quality internationally certified ingredients at reasonable prices.',
    points: [
      'Certified ingredient sourcing from Japan, USA, Europe and more',
      'Assistance confirming ingredient spec sheets and safety certification documents',
      'Bulk purchasing at favorable prices passed on to clients',
      'Import declaration and inspection document assistance',
    ],
    img: 'https://readdy.ai/api/search-image?query=international%20raw%20material%20import%20shipping%20containers%20herbs%20botanical%20extracts%20high%20quality%20natural%20ingredients%20sourcing%20global%20supply%20chain%2C%20professional%20logistics%20health%20supplement%20industry&width=640&height=420&seq=csvc-import-01&orientation=landscape',
    cta: 'Inquire About Sourcing',
    ctaLink: '/en/cooperation/inquiry',
  },
  {
    icon: 'ri-rocket-line',
    tag: '04',
    title: 'Fast and Smooth Market Launch',
    subtitle: 'Small MOQ, rapid market validation, easy product line expansion',
    desc: 'With mature semi-finished product options and small-batch ordering support, brands can quickly expand product lines with lower entry barriers. No need to stock large quantities upfront — launch in small batches to validate sales response, then decide whether to scale up to mass production, minimizing inventory risk.',
    points: [
      'Capsules / tablets MOQ starting from 3,000 bottles',
      'Mature formulas shorten development cycle for rapid launch',
      'Multiple ready-to-use formulas available for selection',
      'Seamless transition to full-scale production after market validation',
    ],
    img: 'https://readdy.ai/api/search-image?query=small%20batch%20supplement%20product%20launch%20health%20brand%20new%20product%20launch%20fast%20to%20market%20concept%20bottles%20capsules%20arranged%20neatly%2C%20clean%20minimal%20product%20photography%20startup%20brand&width=640&height=420&seq=csvc-launch-01&orientation=landscape',
    cta: 'Learn About Small-Batch Orders',
    ctaLink: '/en/cooperation/inquiry',
  },
];

// ─────────────────────────────────────────────
// FAQ page data
// ─────────────────────────────────────────────
const faqCategories = [
  {
    id: 'brand',
    icon: 'ri-store-line',
    label: 'Brand & Marketing',
    faqs: [
      {
        q: "I have no experience with health supplements at all. Is Kang Yong ODM still right for me?",
        a: "Absolutely. Kang Yong has designed a low-barrier ODM process specifically for new brands, e-commerce brands, influencers, and group-buying channels. From market positioning, functional direction, formula design, regulatory review to mass production and launch, our professional team supports you every step of the way.",
      },
      {
        q: "I have a fan base or group-buying channel, but I'm not sure which product to make. Can you help?",
        a: "We'll analyze your audience profile (age, gender, lifestyle, sales context) to help you choose the right functional direction and dosage form — avoiding products that won't sell or carry high regulatory risk.",
      },
      {
        q: "Will ODM products end up too similar to what's already on the market?",
        a: "No. We offer customized formula adjustments and ingredient combination suggestions, avoiding generic off-the-shelf formulas. This gives your product market differentiation and a compelling brand story.",
      },
      {
        q: "I don't have a concrete formula idea yet. Can I still work with Kang Yong?",
        a: "Of course. Based on your sales channels and target audience, we provide market analysis and formula recommendations. For brands at different stages, we offer two core solutions:\n\nExclusive Custom Formulas: developed from scratch based on your needs, with market differentiation and unique competitive advantages\n\nMature Formula Applications: we offer multiple market-validated, highly stable, and consumer-favored best-selling formulas and mature semi-finished products to help you enter the market quickly and painlessly",
      },
    ],
  },
  {
    id: 'risk',
    icon: 'ri-shield-check-line',
    label: 'Small Batches & Risk Control',
    faqs: [
      {
        q: 'I have a limited budget. Can I produce in small quantities?',
        a: 'Yes. We support small trial orders and low-inventory-start plans suitable for new brands, livestream hosts, and group-buying organizers to test market response. For dosage-form-specific minimum order quantity (MOQ) details, please visit the Dosage Forms page.',
      },
      {
        q: "What if the product doesn't sell well?",
        a: 'We help you avoid high-risk functional claims and ingredients early on, and recommend product directions with higher market acceptance to minimize the risk of slow sales.',
      },
      {
        q: 'Can I get samples first to confirm taste and effectiveness?',
        a: 'Yes. Every ODM project can arrange sample trials and formula fine-tuning so you can personally confirm the taste and direction before mass production. Individual product effects may vary depending on product properties and personal constitution; some effects can be verified during the sampling phase. For sampling fee details, please visit the Dosage Forms page.',
      },
    ],
  },
  {
    id: 'regulation',
    icon: 'ri-file-text-line',
    label: 'Regulations & Legal Marketing',
    faqs: [
      {
        q: "If food products can't claim therapeutic effects, how should I write my marketing copy?",
        a: 'We are well-versed in Taiwan food regulations and compliant wording, and can assist in adjusting product names, packaging copy, and sales descriptions to avoid regulatory risks.',
      },
      {
        q: 'Do you assist with food labeling and nutrition facts calculation?',
        a: 'Yes. We can assist with complete food labeling, nutrition facts calculation, and copy review in compliance with food regulations.',
      },
      {
        q: 'Will KOL livestream or group-buying sales scripts violate regulations?',
        a: 'We can provide compliant wording direction and suggestions to help you sell safely on livestream and group-buying platforms.',
      },
    ],
  },
  {
    id: 'channel',
    icon: 'ri-shopping-bag-line',
    label: 'Channels & Sales Support',
    faqs: [
      {
        q: 'Which sales channels are suitable for ODM products?',
        a: 'E-commerce platforms, livestream sales, social media group buying, direct sales systems, pet channels, and healthcare-related organizations are all excellent fits.',
      },
      {
        q: 'Do you provide product education or sales materials?',
        a: 'We can provide product feature descriptions, ingredient highlights, and internal training materials.',
      },
      {
        q: 'Can you produce pet health supplements?',
        a: 'Yes. We have experience in pet health supplement manufacturing, offering formula design for dogs, cats, and rabbits, and producing in compliance with relevant regulations.',
      },
    ],
  },
  {
    id: 'production',
    icon: 'ri-settings-3-line',
    label: 'Production, Lead Time & Quality',
    faqs: [
      {
        q: 'How long does production take?',
        a: 'Standard product production takes approximately 30–60 working days. Initial collaborations may take slightly longer due to more items requiring confirmation. Actual timelines vary based on product complexity.',
      },
      {
        q: 'Does your factory hold international certifications?',
        a: 'Our factory holds international ISO 22000 and HACCP certifications, and can provide production certificates to ensure product manufacturing quality.',
      },
      {
        q: 'Can you support export or cross-border sales?',
        a: 'We can assist with relevant assessments, but further confirmation is required based on target market regulations.',
      },
      {
        q: 'Do you provide third-party testing and certification?',
        a: 'Yes. We can arrange third-party testing (e.g., SGS) according to client requirements.',
      },
      {
        q: 'Do I need to prepare my own packaging and design?',
        a: 'No. We offer complete packaging design and printing integration services — from color boxes, aluminum pouches, and bottles to PTP packaging, all customizable, with assistance in confirming regulatory labeling requirements. For packaging details, please visit the Packaging Design page.',
      },
    ],
  },
  {
    id: 'trust',
    icon: 'ri-shake-hands-line',
    label: 'Partnership & Trust',
    faqs: [
      {
        q: 'Can we sign a Non-Disclosure Agreement (NDA)?',
        a: 'Yes. We value the security of client formulas and business information, and can sign an NDA upon request.',
      },
      {
        q: 'Which types of businesses are best suited to work with Kang Yong?',
        a: 'New brands, e-commerce operators, KOLs and influencers, group-buying organizers, direct sales channels, pet brands, and healthcare-related organizations — all are welcome to consult with us about collaboration.',
      },
    ],
  },
  {
    id: 'export',
    icon: 'ri-global-line',
    label: 'Export & International Markets',
    faqs: [
      {
        q: 'Do I need to adjust formulas or labeling for overseas sales?',
        a: 'It depends on the destination country. Different countries have different requirements for food ingredients, dosage levels, labeling language, and format. We can assist with an early-stage assessment to avoid situations where finished products cannot be exported.',
      },
      {
        q: 'Can you help plan multi-country product versions?',
        a: 'Yes. We can help design multi-version formulas or labeling versions for the same product based on different market requirements, allowing brands to flexibly expand into various international channels.',
      },
    ],
  },
];

// ─────────────────────────────────────────────
// Inquiry page data
// ─────────────────────────────────────────────
const productTypes = [
  'Hard Capsules',
  'Soft Gel Capsules',
  'Tablets',
  'Chewable Tablets',
  'Powder Sachets / Granules',
  'Functional Beverages',
  'Oral Liquid',
  'Gummies',
  'Other / Not Yet Decided',
];

const quantityOptions = [
  'Under 3,000 bottles (sample / trial)',
  '3,000–10,000 bottles',
  '10,000–30,000 bottles',
  '30,000+ bottles',
  'Not yet decided — need advice',
];

const suitableFor = [
  { icon: 'ri-store-2-line', title: 'New Health Supplement Brands', desc: 'Looking to build a brand from scratch and need an OEM partner that supports small-batch orders.' },
  { icon: 'ri-shopping-bag-line', title: 'E-Commerce / Retail Distributors', desc: 'Have an established channel and want to expand their own-brand product line or add items.' },
  { icon: 'ri-global-line', title: 'Export-Oriented Brands', desc: 'Planning to enter overseas markets and need an OEM factory with export documentation support.' },
  { icon: 'ri-team-line', title: 'Brands Needing R&D Support', desc: 'Have a product concept but lack formula expertise and need ODM design & development services.' },
];

const inquiryProcessReminders = [
  { step: '01', icon: 'ri-send-plane-line', text: 'Submit the form or join LINE — sales responds promptly' },
  { step: '02', icon: 'ri-discuss-line', text: 'In-depth consultation to confirm product direction and feasibility' },
  { step: '03', icon: 'ri-draft-line', text: 'Formula proposal and preliminary pricing provided' },
  { step: '04', icon: 'ri-test-tube-line', text: 'Trial sample produced; formal partnership begins after quality confirmation' },
];

const FORM_URL = 'https://readdy.ai/api/form/d7kd2a263r16okiqphkg';

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function EnCooperationSubPage() {
  const location = useLocation();
  const path = location.pathname;

  const seoDataMap: Record<string, { title: string; description: string; keywords: string }> = {
    '/en/cooperation/process': {
      title: 'Cooperation Process | 6-Step OEM ODM Partnership | Kang Yong Biotech',
      description: 'Kang Yong Biotech 6-step cooperation process: needs alignment, formula design, packaging design, testing & certification, mass production confirmation, delivery — clear and transparent.',
      keywords: 'OEM cooperation process, ODM process, health supplement manufacturing steps, partnership Taiwan, Kang Yong process',
    },
    '/en/cooperation/services': {
      title: 'Additional Services | Regulatory, Copywriting & Sourcing | Kang Yong Biotech',
      description: 'Kang Yong additional services: regulatory risk control, marketing copy support, imported ingredient sourcing, and fast market launch support — beyond manufacturing.',
      keywords: 'regulatory service, health supplement copywriting, ingredient sourcing, market launch, additional OEM services',
    },
    '/en/cooperation/faq': {
      title: 'FAQ | Health Supplement OEM ODM Common Questions | Kang Yong Biotech',
      description: 'Frequently asked questions about OEM/ODM health supplement manufacturing: brand building, MOQ, regulations, channels, production timelines, export, and partnership trust.',
      keywords: 'OEM FAQ, ODM FAQ, health supplement questions, MOQ, supplement regulations, brand partnership FAQ',
    },
    '/en/cooperation/inquiry': {
      title: 'Inquiry Now | OEM ODM Health Supplement Consultation | Kang Yong Biotech',
      description: 'Submit your OEM/ODM health supplement inquiry to Kang Yong Biotech. Our team responds promptly to evaluate the best collaboration approach for your brand.',
      keywords: 'OEM inquiry, ODM inquiry, health supplement consultation, manufacturing inquiry, Kang Yong contact form',
    },
  };

  const currentSeo = seoDataMap[path] || seoDataMap['/en/cooperation/process'];
  useSEO({ ...currentSeo, lang: 'en', canonicalPath: path });

  // FAQ state
  const [activeCategory, setActiveCategory] = useState('brand');
  const [openFaqIdx, setOpenFaqIdx] = useState<number | null>(null);

  // Inquiry form state
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const currentCat = faqCategories.find((c) => c.id === activeCategory)!;

  const handleCatChange = (id: string) => {
    setActiveCategory(id);
    setOpenFaqIdx(null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  const subNavItems = [
    { path: '/en/cooperation/process', label: 'Cooperation Process', desc: 'From needs alignment to delivery — every step is clear.' },
    { path: '/en/cooperation/services', label: 'Additional Services', desc: 'Regulatory risk, marketing copy, ingredient sourcing and more.' },
    { path: '/en/cooperation/faq', label: 'FAQ', desc: 'Brand, MOQ, regulations, channels — all common questions answered.' },
    { path: '/en/cooperation/inquiry', label: 'Inquiry Now', desc: 'Fill in the inquiry form to start your first step.' },
  ];

  return (
    <EnLayout>

      {/* ── COOPERATION PROCESS ── */}
      {path === '/en/cooperation/process' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20team%20planning%20project%20roadmap%20on%20whiteboard%20in%20modern%20bright%20office%2C%20health%20supplement%20brand%20collaboration%20meeting%2C%20clean%20minimal%20workspace%20with%20green%20plants%2C%20natural%20daylight&width=1920&height=420&seq=proc-hero-bg-01&orientation=landscape"
                alt="Cooperation Process"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/partnership" className="hover:text-white cursor-pointer">Partnership</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Cooperation Process</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">Partnership</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Cooperation Process</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  From needs alignment to final delivery — a clear, controllable process to help your brand complete product development.
                </p>
              </div>
            </div>
          </section>

          {/* Highlights bar */}
          <section className="bg-white border-b border-gray-100 py-5">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {processHighlights.map((h) => (
                  <div key={h.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className={`${h.icon} text-[#4CAF50] text-base`} />
                    </div>
                    <span className="text-[#1a2e1a] text-[0.875rem] font-medium leading-snug">{h.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Steps */}
          <section className="py-20 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-14">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Six-Step Process</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">From Consultation to Delivery — Every Step Is Clear</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-2xl mx-auto">
                  Dedicated professionals accompany you at every step for a worry-free collaboration
                </p>
              </div>

              {/* Desktop flow */}
              <div className="hidden lg:block">
                <div className="grid grid-cols-3 gap-0 mb-0">
                  {processSteps.slice(0, 3).map((s, idx) => (
                    <div key={s.num} className="relative flex flex-col items-center">
                      {idx < 2 && <div className="absolute top-10 left-1/2 w-full h-0.5 bg-green-200 z-0" />}
                      <div className="relative z-10 flex flex-col items-center px-5 w-full">
                        <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                          <i className={`${s.icon} text-2xl text-[#4CAF50]`} />
                          <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{s.num}</span>
                        </div>
                        <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2 text-center">{s.title}</h3>
                        <p className="text-gray-500 text-[0.875rem] leading-[1.75] text-center mb-3">{s.desc}</p>
                        <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                          <i className="ri-time-line text-sm" />{s.duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end pr-[16.67%] my-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-green-100">
                    <i className="ri-arrow-down-line text-[#4CAF50] text-lg" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-0">
                  {processSteps.slice(3, 6).reverse().map((s, idx) => (
                    <div key={s.num} className="relative flex flex-col items-center">
                      {idx < 2 && <div className="absolute top-10 left-1/2 w-full h-0.5 bg-green-200 z-0" />}
                      <div className="relative z-10 flex flex-col items-center px-5 w-full">
                        <div className="w-20 h-20 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white mb-4 relative">
                          <i className={`${s.icon} text-2xl text-[#4CAF50]`} />
                          <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#4CAF50] text-white text-[0.6875rem] font-bold rounded-full">{s.num}</span>
                        </div>
                        <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2 text-center">{s.title}</h3>
                        <p className="text-gray-500 text-[0.875rem] leading-[1.75] text-center mb-3">{s.desc}</p>
                        <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                          <i className="ri-time-line text-sm" />{s.duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile vertical flow */}
              <div className="lg:hidden space-y-0">
                {processSteps.map((s, idx) => (
                  <div key={s.num} className="flex gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="w-14 h-14 flex items-center justify-center rounded-full border-2 border-[#4CAF50] bg-white relative flex-shrink-0">
                        <i className={`${s.icon} text-xl text-[#4CAF50]`} />
                        <span className="absolute -top-1.5 -right-1.5 w-5 h-5 flex items-center justify-center bg-[#4CAF50] text-white text-[0.625rem] font-bold rounded-full">{s.num}</span>
                      </div>
                      {idx < processSteps.length - 1 && <div className="w-0.5 flex-1 bg-green-200 my-2 min-h-[40px]" />}
                    </div>
                    <div className="pb-8 flex-1 min-w-0">
                      <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-1.5">{s.title}</h3>
                      <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-2">{s.desc}</p>
                      <span className="inline-flex items-center gap-1.5 text-[#4CAF50] text-[0.8125rem] font-medium">
                        <i className="ri-time-line text-sm" />{s.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Detail cards */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-12">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Stage Details</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] mb-4">We Are Here at Every Step</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
                  From the first consultation to finished product handover, Kang Yong's sales and R&D team is with you throughout
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {processSteps.map((s) => (
                  <div key={s.num} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[#4CAF50] font-bold text-[1.75rem] leading-none opacity-20">{s.num}</span>
                      <div className="w-11 h-11 flex items-center justify-center bg-white rounded-xl border border-green-100">
                        <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{s.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75] mb-4">{s.desc}</p>
                    <div className="flex items-center gap-1.5">
                      <i className="ri-time-line text-[#4CAF50] text-sm" />
                      <span className="text-[#4CAF50] text-[0.8125rem] font-medium">{s.duration}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 text-center">
              <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2.125rem] leading-tight mb-4">Ready to Get Started?</h2>
              <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto mb-8">
                Just one message or call is all it takes to get the first step started. The Kang Yong sales team will get back to you promptly.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Start a Consultation
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link to="/en/cooperation/faq" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  View FAQ
                </Link>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More Partnership Pages</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Additional Services', desc: 'Regulatory risk, marketing copy, ingredient sourcing — learn about all our value-added services.', href: '/en/cooperation/services' },
                  { title: 'FAQ', desc: 'Brand, MOQ, regulations, channels — all common questions answered.', href: '/en/cooperation/faq' },
                  { title: 'Inquiry Now', desc: 'Fill in the inquiry form to start your first step.', href: '/en/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── ADDITIONAL SERVICES ── */}
      {path === '/en/cooperation/services' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[360px] md:min-h-[420px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=diverse%20professional%20services%20health%20biotech%20company%20offering%20multiple%20solutions%20consultation%20meeting%20team%20of%20experts%20in%20modern%20bright%20office%2C%20collaborative%20atmosphere%20clean%20minimal%20interior&width=1920&height=420&seq=csvc-hero-bg-01&orientation=landscape"
                alt="Additional Services"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/partnership" className="hover:text-white cursor-pointer">Partnership</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Additional Services</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">Partnership</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Additional Services</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  Beyond contract manufacturing, Kang Yong provides multiple value-added services to prepare your brand from regulations and copy to full market launch.
                </p>
              </div>
            </div>
          </section>

          {/* Services */}
          <section className="py-20 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10 space-y-20">
              {coopServices.map((svc, idx) => (
                <div key={svc.tag} className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 lg:gap-16 items-center`}>
                  <div className="w-full lg:w-1/2">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="text-[#4CAF50] font-bold text-[1.75rem] leading-none opacity-20">{svc.tag}</span>
                      <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl border border-green-200">
                        <i className={`${svc.icon} text-[#4CAF50] text-xl`} />
                      </div>
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] leading-tight mb-2">{svc.title}</h2>
                    <p className="text-[#4CAF50] font-medium text-[1rem] mb-5">{svc.subtitle}</p>
                    <p className="text-gray-600 text-[1.0625rem] leading-[1.75] mb-6">{svc.desc}</p>
                    <ul className="space-y-3 mb-7">
                      {svc.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-fill text-[#4CAF50] text-lg" />
                          </div>
                          <span className="text-gray-600 text-[0.9375rem] leading-[1.75]">{pt}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={svc.ctaLink} className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer">
                      {svc.cta}
                      <i className="ri-arrow-right-line" />
                    </Link>
                  </div>
                  <div className="w-full lg:w-1/2">
                    <div className="rounded-2xl overflow-hidden">
                      <img src={svc.img} alt={svc.title} className="w-full h-[300px] lg:h-[360px] object-cover object-top" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Summary cards */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-10">
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] md:text-[2rem] mb-3">More Than Manufacturing — Your End-to-End Partner</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
                  From regulatory to copy, from ingredients to market launch — expert support at every step
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                {coopServices.map((svc) => (
                  <div key={svc.tag} className="bg-white rounded-xl p-5 border border-gray-100 text-center hover:border-green-200 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-xl mx-auto mb-3 border border-green-100">
                      <i className={`${svc.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem]">{svc.title}</h3>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1.0625rem] px-10 py-4 rounded-full transition-all duration-300 cursor-pointer">
                  Inquire About Our Services
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More Partnership Pages</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Cooperation Process', desc: 'From needs alignment to delivery — every step clearly explained.', href: '/en/cooperation/process' },
                  { title: 'Regulatory Risk', desc: 'Understand key health food regulations to prevent risk from the source.', href: '/en/about/regulation' },
                  { title: 'Packaging', desc: 'Packaging materials, visual design, and regulatory labeling — one-stop packaging solution.', href: '/en/services/packaging' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── FAQ ── */}
      {path === '/en/cooperation/faq' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[320px] md:min-h-[380px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=person%20reading%20FAQ%20document%20health%20supplement%20product%20brochure%20on%20clean%20white%20desk%20with%20green%20plant%2C%20professional%20customer%20support%20service%20concept%2C%20bright%20natural%20lighting%20minimal&width=1920&height=380&seq=faq-hero-bg-01&orientation=landscape"
                alt="FAQ"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-24">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/partnership" className="hover:text-white cursor-pointer">Partnership</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">FAQ</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">Partnership</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">FAQ</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  The most frequently asked questions from brand clients, organized by topic for easy reference.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Layout */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Left: categories */}
                <div className="w-full lg:w-[260px] flex-shrink-0">
                  <h2 className="text-[#1a2e1a] font-bold text-[1rem] mb-4 px-1">Categories</h2>
                  <div className="flex flex-row lg:flex-col flex-wrap gap-2">
                    {faqCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCatChange(cat.id)}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-xl text-left transition-all duration-200 cursor-pointer whitespace-nowrap ${activeCategory === cat.id ? 'bg-[#4CAF50] text-white' : 'bg-white text-gray-600 border border-gray-100 hover:border-green-200 hover:text-[#4CAF50]'}`}
                      >
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`${cat.icon} text-sm`} />
                        </div>
                        <span className="text-[0.875rem] font-medium">{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Right: FAQs */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-9 h-9 flex items-center justify-center bg-[#4CAF50] rounded-xl">
                      <i className={`${currentCat.icon} text-white text-base`} />
                    </div>
                    <h2 className="text-[#1a2e1a] font-bold text-[1.25rem]">{currentCat.label}</h2>
                    <span className="text-gray-400 text-[0.8125rem]">{currentCat.faqs.length} questions</span>
                  </div>
                  <div className="space-y-3">
                    {currentCat.faqs.map((faq, idx) => (
                      <div
                        key={idx}
                        className={`bg-white rounded-xl border overflow-hidden transition-all duration-200 ${openFaqIdx === idx ? 'border-[#4CAF50]/40' : 'border-gray-100 hover:border-green-200'}`}
                      >
                        <button
                          className="w-full flex items-start justify-between px-6 py-5 text-left cursor-pointer"
                          onClick={() => setOpenFaqIdx(openFaqIdx === idx ? null : idx)}
                        >
                          <span className={`text-[1rem] font-semibold leading-snug pr-4 ${openFaqIdx === idx ? 'text-[#4CAF50]' : 'text-[#1a2e1a]'}`}>{faq.q}</span>
                          <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 transition-all duration-200 ${openFaqIdx === idx ? 'bg-[#4CAF50] text-white' : 'bg-green-50 text-[#4CAF50]'}`}>
                            <i className={`text-sm ${openFaqIdx === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                          </div>
                        </button>
                        {openFaqIdx === idx && (
                          <div className="px-6 pb-5 border-t border-gray-50">
                            <p className="text-gray-600 text-[0.9375rem] leading-[1.8] pt-4">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Still have questions */}
          <section className="py-16 bg-white">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="bg-[#f0faf0] border border-green-100 rounded-3xl px-8 lg:px-14 py-12 flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] leading-tight mb-3">Didn't Find Your Answer?</h2>
                  <p className="text-gray-500 text-[1.0625rem] leading-[1.75]">
                    Feel free to contact the Kang Yong sales team directly — we respond to all inquiries promptly.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 flex-shrink-0">
                  <Link to="/en/contact/form" className="whitespace-nowrap inline-flex items-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    Ask Us Directly
                    <i className="ri-arrow-right-line" />
                  </Link>
                  <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center gap-2 border border-[#4CAF50] text-[#4CAF50] hover:bg-[#4CAF50] hover:text-white font-semibold text-[1rem] px-8 py-4 rounded-full transition-all duration-300 cursor-pointer">
                    <i className="ri-line-fill" /> LINE Quick Chat
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-[#f7faf7] border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More Partnership Pages</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'Cooperation Process', desc: 'From needs alignment to delivery — every step clearly explained.', href: '/en/cooperation/process' },
                  { title: 'Inquiry Now', desc: 'Fill in the inquiry form — Kang Yong responds promptly.', href: '/en/cooperation/inquiry' },
                  { title: 'Contact Form', desc: 'Fill in the inquiry form to start your partnership.', href: '/en/contact/form' },
                ].map((r) => (
                  <Link key={r.href} to={r.href} className="bg-white rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── INQUIRY ── */}
      {path === '/en/cooperation/inquiry' && (
        <>
          {/* Hero */}
          <section className="relative pt-20 min-h-[340px] md:min-h-[400px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20consultation%20handshake%20meeting%20health%20supplement%20brand%20partnership%2C%20modern%20bright%20office%20with%20green%20plants%2C%20warm%20welcoming%20atmosphere%2C%20clean%20minimal%20workspace%20natural%20light&width=1920&height=400&seq=inquiry-hero-bg-01&orientation=landscape"
                alt="Inquiry Now"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0d2210]/94 via-[#0d2210]/74 to-[#0d2210]/42" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d2210]/70 via-transparent to-[#0d2210]/28" />
            </div>
            <div className="relative z-10 w-full max-w-screen-xl mx-auto px-6 lg:px-10 pb-14 pt-28">
              <div className="max-w-2xl">
                <nav className="flex items-center gap-1.5 mb-4 text-gray-300 text-[0.8125rem]">
                  <Link to="/en" className="hover:text-white cursor-pointer">Home</Link>
                  <i className="ri-arrow-right-s-line" />
                  <Link to="/en/partnership" className="hover:text-white cursor-pointer">Partnership</Link>
                  <i className="ri-arrow-right-s-line" />
                  <span className="text-white/90">Inquiry Now</span>
                </nav>
                <span className="inline-block bg-[#4CAF50]/25 border border-[#4CAF50]/50 text-green-300 text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-5">Partnership</span>
                <h1 className="text-white font-bold text-[2.25rem] md:text-[2.875rem] leading-tight mb-4">Inquiry Now</h1>
                <p className="text-gray-200 text-[1.0625rem] leading-[1.75] max-w-xl">
                  Share your requirements and Kang Yong will help you evaluate the most suitable ODM / OEM collaboration approach.
                </p>
              </div>
            </div>
          </section>

          {/* Suitable for */}
          <section className="py-14 bg-white border-b border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="text-center mb-10">
                <span className="inline-block bg-green-50 text-[#4CAF50] text-[0.875rem] font-medium px-4 py-1.5 rounded-full mb-4 border border-green-200">Who Is This For</span>
                <h2 className="text-[#1a2e1a] font-bold text-[1.75rem] mb-3">Which Brands Should Reach Out?</h2>
                <p className="text-gray-500 text-[1.0625rem] leading-[1.75] max-w-xl mx-auto">
                  Whether you are a brand just starting out or an established brand expanding your product line, Kang Yong has a solution for you
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {suitableFor.map((s) => (
                  <div key={s.title} className="bg-[#f7faf7] rounded-2xl p-6 border border-gray-100 hover:border-green-200 hover:-translate-y-1 transition-all duration-300">
                    <div className="w-12 h-12 flex items-center justify-center bg-white rounded-xl border border-green-100 mb-4">
                      <i className={`${s.icon} text-[#4CAF50] text-xl`} />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1rem] mb-2">{s.title}</h3>
                    <p className="text-gray-500 text-[0.875rem] leading-[1.75]">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Main content: form + contact info */}
          <section className="py-16 bg-[#f7faf7]">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 items-start">

                {/* Left: contact info */}
                <div className="w-full lg:w-[320px] flex-shrink-0 space-y-5">
                  <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-2">Other Contact Methods</h2>

                  {/* LINE */}
                  <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-line-fill text-[#4CAF50] text-xl" />
                      <p className="text-[#1a2e1a] font-bold text-[1rem]">LINE Official Account</p>
                    </div>
                    <p className="text-[#1a2e1a] text-[0.875rem] font-medium mb-1">@126uwznp — Quick Chat</p>
                    <p className="text-gray-600 text-[0.8125rem] leading-[1.75] mb-4">
                      Join Kang Yong's LINE official account for instant messaging — our sales team responds quickly.
                    </p>
                    <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.9375rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full">
                      <i className="ri-line-fill text-lg" /> Join LINE Now
                    </a>
                  </div>

                  {/* Phone */}
                  <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className="ri-phone-line text-[#4CAF50] text-lg" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-[0.75rem] mb-0.5">Phone</p>
                      <a href="tel:+886422029858" className="text-[#1a2e1a] font-semibold text-[0.9375rem] hover:text-[#4CAF50] transition-colors cursor-pointer">+886-4-2202-9858</a>
                      <p className="text-gray-400 text-[0.75rem] mt-1">Mon–Fri 09:00–18:00 (UTC+8)</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-100">
                    <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                      <i className="ri-mail-line text-[#4CAF50] text-lg" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-[0.75rem] mb-0.5">Email</p>
                      <a href="mailto:natural1070606@gmail.com" className="text-[#1a2e1a] font-semibold text-[0.875rem] hover:text-[#4CAF50] transition-colors cursor-pointer break-all">natural1070606@gmail.com</a>
                    </div>
                  </div>

                  {/* Process reminder */}
                  <div className="bg-white rounded-xl p-5 border border-gray-100">
                    <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-4 flex items-center gap-2">
                      <i className="ri-map-pin-time-line text-[#4CAF50]" /> What Happens After Inquiry
                    </p>
                    <div className="space-y-3">
                      {inquiryProcessReminders.map((r) => (
                        <div key={r.step} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center bg-green-50 text-[#4CAF50] text-[0.6875rem] font-bold rounded-full flex-shrink-0 mt-0.5 border border-green-100">{r.step}</span>
                          <span className="text-gray-600 text-[0.8125rem] leading-[1.75]">{r.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right: form */}
                <div className="w-full lg:flex-1">
                  <h2 className="text-[#1a2e1a] font-bold text-[1.5rem] mb-6">Submit Your Inquiry</h2>

                  {submitState === 'success' ? (
                    <div className="flex flex-col items-center text-center bg-white rounded-2xl border border-green-100 overflow-hidden">
                      <div className="w-full px-8 pt-12 pb-8">
                        <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                          <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                        </div>
                        <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">Inquiry Submitted — Thank You!</h3>
                        <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">
                          Kang Yong will contact you shortly to help confirm your product direction, dosage form, estimated quantity, and collaboration approach.
                        </p>
                      </div>
                      <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                        <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">Want a Faster Response?</p>
                        <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">You can also join LINE now for a dedicated specialist to help confirm your requirements.</p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                          <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto">
                            <i className="ri-line-fill text-lg" /> Join LINE Now
                          </a>
                          <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@ ID: @126uwznp</span>
                        </div>
                      </div>
                      <div className="px-8 py-5">
                        <button onClick={() => setSubmitState('idle')} className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer">
                          Submit Again
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-7 space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">Name <span className="text-red-500">*</span></label>
                          <input name="contact_name" type="text" required placeholder="Your full name" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">Company / Brand Name</label>
                          <input name="company_name" type="text" placeholder="Your company or brand name" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">Phone <span className="text-red-500">*</span></label>
                          <input name="phone" type="tel" required placeholder="Your contact number" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                        <div>
                          <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">Email <span className="text-red-500">*</span></label>
                          <input name="email" type="email" required placeholder="Your email address" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">Product Type of Interest</label>
                        <select name="product_type" defaultValue="" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer">
                          <option value="" disabled>Select a product type</option>
                          {productTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">Estimated Quantity</label>
                        <select name="quantity" defaultValue="" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer">
                          <option value="" disabled>Select an estimated quantity</option>
                          {quantityOptions.map((q) => <option key={q} value={q}>{q}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                          Requirements <span className="text-gray-400 text-[0.75rem] font-normal">({charCount}/500)</span>
                        </label>
                        <textarea
                          name="requirement"
                          rows={5}
                          maxLength={500}
                          onChange={(e) => setCharCount(e.target.value.length)}
                          placeholder="Briefly describe your needs — e.g. product concept, target health benefits, target audience, collaboration type or other details"
                          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                        />
                        {charCount > 500 && <p className="text-red-500 text-[0.75rem] mt-1">Requirements must not exceed 500 characters</p>}
                      </div>
                      <button type="submit" disabled={submitState === 'loading' || charCount > 500} className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer">
                        {submitState === 'loading' ? (<><i className="ri-loader-4-line animate-spin text-lg" /> Submitting…</>) : (<><i className="ri-send-plane-line text-lg" /> Submit Inquiry</>)}
                      </button>
                      {submitState === 'error' && <p className="text-red-500 text-[0.875rem] text-center">Submission failed. Please try again or contact us directly.</p>}
                      <p className="text-gray-400 text-[0.8125rem] text-center leading-[1.75]">
                        By submitting this form you agree to have your personal information processed in accordance with our Privacy Policy to provide the requested service response.
                      </p>
                      <div className="border-t border-gray-100 pt-5 mt-1">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#f7faf7] rounded-xl px-5 py-4 border border-gray-100">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center bg-[#e8f5e9] rounded-full border border-[#c8e6c9] flex-shrink-0">
                              <i className="ri-line-fill text-[#4CAF50] text-lg" />
                            </div>
                            <div>
                              <p className="text-[#1a2e1a] font-semibold text-[0.875rem]">Want a faster response?</p>
                              <p className="text-gray-500 text-[0.8125rem]">Join LINE after submitting — LINE@ ID: <span className="text-[#4CAF50] font-medium">@126uwznp</span></p>
                            </div>
                          </div>
                          <a href="https://lin.ee/X7Hcpf4" rel="nofollow" className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-5 py-2.5 rounded-full transition-all duration-300 cursor-pointer flex-shrink-0 w-full sm:w-auto">
                            <i className="ri-line-fill" /> Join LINE Now
                          </a>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Related */}
          <section className="py-12 bg-white border-t border-gray-100">
            <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
              <h3 className="text-[#1a2e1a] font-bold text-[1.125rem] mb-5">More Partnership Pages</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { title: 'FAQ', desc: 'Brand, MOQ, regulations, channels — all common questions answered.', href: '/en/cooperation/faq' },
                  { title: 'OEM Services', desc: 'Hot formulas, dosage forms, packaging design — learn more about our services.', href: '/en/services/hot-formulas' },
                  { title: 'LINE Chat', desc: 'Join LINE for a quick consultation — our team responds in real time.', href: 'https://lin.ee/X7Hcpf4' },
                ].map((r) => (
                  r.href.startsWith('http')
                    ? <a key={r.href} href={r.href} rel="nofollow" className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                        <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                        <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                      </a>
                    : <Link key={r.href} to={r.href} className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100 hover:border-green-200 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer block">
                        <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-1">{r.title}</p>
                        <p className="text-gray-500 text-[0.8125rem] leading-[1.6]">{r.desc}</p>
                      </Link>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── FALLBACK ── */}
      {!['process', 'services', 'faq', 'inquiry'].some(p => path.includes(p)) && (
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {subNavItems.map((item) => (
                <Link key={item.path} to={item.path} className="bg-[#f7faf7] rounded-2xl p-7 border border-gray-100 hover:border-green-200 transition-all duration-200 cursor-pointer block">
                  <p className="text-[#1a2e1a] font-bold text-[1.0625rem] mb-2">{item.label}</p>
                  <p className="text-gray-500 text-[0.9375rem] leading-[1.75]">{item.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </EnLayout>
  );
}
